import type {
  FlightState,
  Pilot,
  PilotSeed,
  Enemy,
  Bullet,
  UpgradeId,
  FlightAction,
  FlightCommand,
  MainWeapon,
} from "./types";
import { FIELD, WORLD_WIDTH } from "./types";
import { cameraOffset } from "./viewport";
import {
  UPGRADES,
  weaponInfo,
  MAIN_WEAPONS,
  EVOLUTIONS,
  CHAPTERS,
  xpRequired,
} from "./content";
import * as legacy from "./engine-v1";
export { segmentDistance } from "./engine-v1";
const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));
const q = (n: number) => Math.round(n * 1024) / 1024;
export const worldWidth = (s: FlightState) =>
  s.version === 2 ? WORLD_WIDTH : FIELD.width;
export const cameraLeft = (s: FlightState, id?: string) =>
  s.version === 2
    ? cameraOffset(
        (s.pilots.find((p) => p.id === id) ?? s.pilots[0]).x,
        worldWidth(s),
      )
    : 0;
export const rank = (p: Pilot, id: UpgradeId) => p.levels[id] ?? 0;
const sides = ["wing", "orbit", "homing", "boomerang", "split", "arc"] as const;
const evolved = (p: Pilot) => rank(p, `evolve-${p.main}`) > 0;
function random(s: FlightState) {
  let x = s.rng | 0;
  x ^= x << 13;
  x ^= x >>> 17;
  x ^= x << 5;
  s.rng = x >>> 0;
  return s.rng / 4294967296;
}
export function createFlight(
  id: string,
  seed: number,
  pilots: PilotSeed[],
  version: 1 | 2 = 2,
): FlightState {
  const s = legacy.createFlight(id, seed, pilots);
  if (version === 1) return s;
  s.version = 2;
  s.nextBoss = 10800;
  s.spawnAt = 90;
  s.director = {
    wave: 0,
    event: "formation",
    eventUntil: 2400,
    chapter: 0,
    pressure: 0,
    lastUpgrade: 0,
    spawnSerial: 0,
    killsAt: 0,
    eventTarget: 8,
    eventDone: false,
  };
  s.pickups = [];
  for (const [i, p] of s.pilots.entries()) {
    p.x = p.tx =
      pilots.length === 1 ? WORLD_WIDTH / 2 : WORLD_WIDTH / 2 - 100 + i * 200;
    p.rerolls = 2;
    p.recentChoices = [];
    p.combo = 0;
    p.comboUntil = 0;
    p.overdriveUntil = 0;
    p.damageDealt = 0;
    p.hitsTaken = 0;
  }
  s.banner = { text: "晨光云海 · 编队突破", until: 200 };
  return s;
}
export function validUpgrades(p: Pilot) {
  const count = sides.filter((id) => rank(p, id)).length;
  return UPGRADES.filter(
    (u) =>
      (!u.cap || rank(p, u.id) < u.cap) &&
      (!u.main || u.main === p.main) &&
      (!u.requires || u.requires.every((id) => rank(p, id) >= 2)) &&
      (u.group !== "副武器" ||
        (p.unlocked.includes(u.id as any) &&
          (rank(p, u.id) > 0 || count < 3))) &&
      (u.id !== "repair" || (p.hp > 0 && p.hp < p.maxHp)) &&
      (u.id !== "ricochet" || !["laser", "rail", "tesla"].includes(p.main)) &&
      (u.id !== "pierce" || p.main !== "tesla") &&
      (u.id !== "velocity" || !["tesla","laser"].includes(p.main)) &&
      (u.id !== "size" || p.main !== "tesla") &&
      (u.id !== "blast" ||
        p.main === "rocket" ||
        rank(p, "split") > 0 ||
        rank(p, "evolve-flame") > 0) &&
      (u.id !== "chain" || p.main === "tesla" || rank(p, "arc") > 0),
  );
}
function offer(s: FlightState, p: Pilot) {
  const pool = validUpgrades(p),
    chosen: UpgradeId[] = [];
  const take = (candidates: typeof pool) => {
    if (!candidates.length) return;
    const preferred = candidates.filter(
        (u) => !p.recentChoices?.includes(u.id),
      ),
      source = preferred.length ? preferred : candidates,
      u = source[Math.floor(random(s) * source.length)];
    chosen.push(u.id);
    pool.splice(pool.indexOf(u), 1);
  };
  take(pool.filter((u) => u.group === "进化"));
  if (!chosen.length)
    take(
      pool.filter(
        (u) =>
          (EVOLUTIONS[p.main].requires.includes(u.id) && rank(p, u.id) < 2) ||
          u.main === p.main ||
          (sides.includes(u.id as any) && rank(p, u.id) > 0),
      ),
    );
  take(pool.filter((u) => u.group === "副武器" || u.group === "弹道"));
  while (chosen.length < 3 && pool.length) take(pool);
  p.choices = chosen;
  p.recentChoices = chosen;
}
function effect(
  s: FlightState,
  x: number,
  y: number,
  kind: FlightState["effects"][number]["kind"],
  label?: string,
  toX?: number,
  toY?: number,
) {
  s.effects.push({
    id: s.nextId++,
    x,
    y,
    age: 0,
    kind,
    ...(label ? { label } : {}),
    ...(toX === undefined ? {} : { toX }),
    ...(toY === undefined ? {} : { toY }),
  });
  if (s.effects.length > 70) s.effects.shift();
}
function alive(s: FlightState) {
  return s.pilots.filter((p) => !p.left && p.hp > 0);
}
function checkEnd(s: FlightState) {
  if (!alive(s).length) {
    s.phase = "ended";
    s.reason = "战机被击落";
  } else if (
    s.phase === "choosing" &&
    s.pilots.filter((p) => !p.left).every((p) => !p.choices.length)
  )
    s.phase = "playing";
}
export function flightAction(
  s: FlightState,
  id: string,
  a: FlightAction,
): boolean {
  if (s.version === 1) {
    if (a.kind === "reroll" || a.kind === "swap") return false;
    return legacy.flightAction(s, id, a);
  }
  const p = s.pilots.find((p) => p.id === id && !p.left);
  if (!p || s.phase === "ended") return false;
  if (a.kind === "leave") {
    p.left = true;
    p.hp = 0;
    p.choices = [];
    checkEnd(s);
    return true;
  }
  if (a.kind === "end") {
    if (s.mode !== "solo") return false;
    s.phase = "ended";
    s.reason = "主动结束";
    return true;
  }
  if (a.kind === "reroll") {
    if (s.phase !== "choosing" || !p.choices.length || !p.rerolls) return false;
    p.rerolls--;
    offer(s, p);
    return true;
  }
  if (a.kind === "choose") {
    if (s.phase !== "choosing" || !p.choices.includes(a.upgrade)) return false;
    const id = a.upgrade;
    p.levels[id] = rank(p, id) + 1;
    p.choices = [];
    if (id === "health") {
      p.maxHp += 30;
      if (p.hp > 0) p.hp += 30;
    }
    if (id === "shield") p.shield += 40;
    if (id === "repair" && p.hp > 0) {
      p.hp = Math.min(p.maxHp, p.hp + p.maxHp * 0.5);
      p.invulnerable = 120;
    }
    if (id.startsWith("evolve-")) {
      effect(s, p.x, p.y, "evolve", EVOLUTIONS[p.main].name);
      s.effects[s.effects.length - 1].target = p.id;
      s.banner = {
        text: EVOLUTIONS[p.main].name + " · 进化完成",
        until: s.tick + 240,
      };
    } else effect(s, p.x, p.y, "supply", "强化 +1");
    checkEnd(s);
    return true;
  }
  if (s.phase !== "playing" || p.hp <= 0) return false;
  if (a.kind === "move") {
    if (!Number.isFinite(a.x) || !Number.isFinite(a.y)) return false;
    p.x = p.tx = q(clamp(a.x, 28, WORLD_WIDTH - 28));
    p.y = p.ty = q(clamp(a.y, 80, 1200));
    return true;
  }
  if (a.kind === "swap") {
    if (!p.offeredWeapon) return false;
    p.main = p.offeredWeapon;
    p.offeredWeapon = undefined;
    p.fireAt = s.tick;
    effect(s, p.x, p.y, "supply", weaponInfo(p.main).name);
    return true;
  }
  if (p.cooldown > 0) return false;
  p.cooldown = Math.max(1200, 2700 - rank(p, "cooldown") * 300);
  s.bullets = s.bullets.filter((b) => b.owner !== "enemy");
  effect(s, p.x, p.y, "ink");
  s.effects[s.effects.length - 1].target = p.id;
  for (const e of [...s.enemies]) damage(s, e, 160 * attack(s, p), p);
  return true;
}
export function difficulty(s: FlightState) {
  const minute = s.tick / 3600,
    boss = !!s.enemies.find((e) => e.boss);
  return {
    enemyCap: boss ? 3 : Math.min(20, 8 + Math.floor(minute / 2)),
    bulletCap:
      minute < 3
        ? 38
        : minute < 6
          ? 64
          : Math.min(144, 72 + Math.floor(minute) * 4),
    spawnInterval: Math.max(62, 150 - minute * 4),
    health:
      1 +
      minute * 0.055 +
      Math.max(0, minute - 5) * 0.13 +
      Math.max(0, minute - 12) * 0.25,
    bulletSpeed: Math.min(4.5, 1.8 + minute * 0.08),
  };
}
function attack(s: FlightState, p: Pilot) {
  return (
    (1 + rank(p, "attack") * 0.25) * (s.tick < (p.overdriveUntil ?? 0) ? 2 : 1)
  );
}
function bullet(
  s: FlightState,
  owner: string,
  kind: string,
  x: number,
  y: number,
  vx: number,
  vy: number,
  damage: number,
  radius = 7,
  life = 180,
  pierce = 0,
  extra: Partial<Bullet> = {},
) {
  const enemy = owner === "enemy";
  if (
    s.bullets.filter((b) => (b.owner === "enemy") === enemy && b.life > 0)
      .length >= (enemy ? difficulty(s).bulletCap : 280)
  )
    return;
  s.bullets.push({
    id: s.nextId++,
    owner,
    kind,
    x: q(x),
    y: q(y),
    vx: q(vx),
    vy: q(vy),
    radius,
    damage,
    life,
    pierce,
    hits: [],
    age: 0,
    ...extra,
  });
}
function drop(
  s: FlightState,
  x: number,
  y: number,
  kind: "repair" | "shield" | "power" | "weapon",
  weapon?: MainWeapon,
) {
  if (s.pickups!.length >= 14) return;
  s.pickups!.push({
    id: s.nextId++,
    x,
    y,
    kind,
    ...(weapon ? { weapon } : {}),
    life: 900,
  });
}
function spawn(
  s: FlightState,
  kind: number,
  boss = false,
  x?: number,
  y = -65,
  elite = false,
) {
  const d = difficulty(s);
  if (s.enemies.length >= (boss ? 24 : d.enemyCap)) return;
  const team = s.pilots.filter((p) => !p.left).length > 1 ? 1.65 : 1,
    base = boss
      ? 1350 + s.bosses * 500
      : kind === 5
        ? 55
        : kind === 7
          ? 65
          : kind >= 16
            ? 75
            : kind === 15
              ? 95
              : 22 + (kind % 5) * 4;
  const hp = base * d.health * team * (elite ? 3 : 1),
    px =
      x ??
      (boss
        ? alive(s).reduce((n, p) => n + p.x, 0) / Math.max(1, alive(s).length)
        : clamp(
            (alive(s)[0]?.x ?? 720) + (random(s) - 0.5) * 620,
            50,
            WORLD_WIDTH - 50,
          ));
  s.enemies.push({
    id: s.nextId++,
    kind,
    x: px,
    startX: px,
    y,
    startY: y,
    altitude: kind >= 16 ? "ground" : "air",
    route:
      kind >= 16
        ? "ground"
        : kind === 1 || kind === 11
          ? "orbit"
          : kind === 2 || kind === 14
            ? "sweep"
            : kind === 8
              ? "dive"
              : kind === 3
                ? "pincer"
                : "dive",
    hp,
    maxHp: hp,
    age: 0,
    fireAt: 120 + Math.floor(random(s) * 70),
    warnAt: 0,
    phase: random(s) * Math.PI * 2,
    scale: d.health,
    boss,
    radius: boss ? 74 : elite ? 36 : kind === 15 ? 33 : 23,
    elite,
  });
}
function damage(
  s: FlightState,
  e: Enemy,
  value: number,
  p?: Pilot,
  dot = false,
) {
  if (!s.enemies.includes(e)) return;
  let power =
    value * (p && (e.boss || e.elite) ? 1 + rank(p, "precision") * 0.35 : 1);
  if (p && p.main === "rail" && e.boss && evolved(p)) power *= 1.35;
  const actual = Math.min(e.hp, power);
  e.hp -= power;
  if (p) {
    p.damageDealt = (p.damageDealt ?? 0) + actual;
    if (!dot) {
      if (rank(p, "burn") || p.main === "flame")
        e.burnUntil = s.tick + 180 + rank(p, "burn") * 60;
      if (rank(p, "frost") || (p.main === "laser" && evolved(p)))
        e.slowUntil = s.tick + 100;
      if (!e.boss) e.y -= rank(p, "push") * 4;
    }
    e.source = p.id;
    if (rank(p, "execution") && !e.boss && e.hp / e.maxHp < 0.12) e.hp = 0;
  }
  if (e.hp > 0) {
    if (s.tick % 3 === 0) effect(s, e.x, e.y, "hit");
    return;
  }
  s.enemies.splice(s.enemies.indexOf(e), 1);
  s.kills++;
  const xp = e.boss
    ? 100
    : e.elite
      ? 45
      : e.kind === 15
        ? 35
        : 8 + (e.kind % 3);
  s.xp +=
    xp *
    (1 +
      Math.max(
        0,
        ...s.pilots.filter((p) => !p.left).map((p) => rank(p, "experience")),
      ) *
        0.2);
  effect(s, e.x, e.y, "burst");
  if (p) {
    p.combo = s.tick < (p.comboUntil ?? 0) ? (p.combo ?? 0) + 1 : 1;
    p.comboUntil = s.tick + 240;
    if (rank(p, "overdrive") && p.combo % 10 === 0) {
      p.overdriveUntil = s.tick + 360;
      effect(s, p.x, p.y, "evolve", "连击超载");
    }
  }
  if (e.boss) {
    effect(s, e.x, e.y, "boss");
    s.bosses++;
    s.scene = s.bosses % CHAPTERS.length;
    s.director!.chapter = s.scene;
    s.nextBoss = s.tick + 10800;
    s.director!.event = "recovery";
    s.director!.eventUntil = s.tick + 900;
    s.director!.eventDone = true;
    s.bullets = s.bullets.filter((b) => b.owner !== "enemy");
    s.enemies = [];
    drop(s, e.x - 90, 410, "repair");
    drop(s, e.x + 90, 410, "power");
    drop(s, e.x, 340, "weapon", MAIN_WEAPONS[(s.bosses + 2) % 8].id);
    for (const p of s.pilots) p.rerolls = Math.min(3, (p.rerolls ?? 0) + 1);
    s.banner = {
      text: CHAPTERS[s.scene].name + " · 抵达新航域",
      until: s.tick + 260,
    };
  } else if (e.kind === 15 || e.elite) {
    if (
      (e.elite && s.director!.event === "elite") ||
      (e.kind === 15 && s.director!.event === "convoy")
    )
      s.director!.eventTarget = -1;
    drop(
      s,
      e.x,
      e.y,
      e.kind === 15 ? "weapon" : "power",
      MAIN_WEAPONS[Math.floor(random(s) * 8)].id,
    );
    if (e.elite) drop(s, e.x + 45, e.y, "repair");
  } else {
    if (e.kind === 6)
      for (const dx of [-30, 30])
        spawn(s, 0, false, clamp(e.x + dx, 40, WORLD_WIDTH - 40), e.y);
    if (
      s.kills %
        Math.max(
          9,
          17 - Math.max(...s.pilots.map((p) => rank(p, "experience"))),
        ) ===
      0
    )
      drop(s, e.x, e.y, s.kills % 2 ? "repair" : "shield");
  }
  if (p && p.main === "flame" && evolved(p) && !dot)
    for (const nearby of [...s.enemies])
      if (Math.hypot(nearby.x - e.x, nearby.y - e.y) < 110)
        damage(s, nearby, power * 0.35, p, true);
}
function hurt(s: FlightState, p: Pilot, value: number) {
  if (p.hp <= 0 || p.invulnerable || p.left) return;
  let d = value * (1 - rank(p, "armor") * 0.08),
    absorbed = Math.min(d, p.shield);
  p.shield -= absorbed;
  d -= absorbed;
  p.hp = Math.max(0, p.hp - d);
  p.invulnerable = 75 + rank(p, "grace") * 18;
  p.hitsTaken = (p.hitsTaken ?? 0) + 1;
  p.combo = 0;
  effect(s, p.x, p.y, "hurt");
  s.effects[s.effects.length - 1].target = p.id;
}
function chain(
  s: FlightState,
  p: Pilot,
  x: number,
  y: number,
  power: number,
  jumps: number,
  range: number,
) {
  const hit = new Set<number>();
  for (let i = 0; i < jumps; i++) {
    const e = s.enemies
      .filter(
        (e) =>
          !hit.has(e.id) && e.y > 0 && Math.hypot(e.x - x, e.y - y) < range,
      )
      .sort(
        (a, b) =>
          Math.hypot(a.x - x, a.y - y) - Math.hypot(b.x - x, b.y - y) ||
          a.id - b.id,
      )[0];
    if (!e) break;
    effect(s, x, y, "chain", undefined, e.x, e.y);
    hit.add(e.id);
    x = e.x;
    y = e.y;
    damage(s, e, power * Math.pow(0.86, i), p);
    range = 250 + rank(p, "chain") * 35;
  }
}
export function shootPilot(s: FlightState, p: Pilot) {
  if (s.tick < p.fireAt) return;
  const w = weaponInfo(p.main),
    tune = rank(p, `tune-${p.main}`),
    evo = evolved(p),
    lanes = rank(p, "lanes"),
    bursts = rank(p, "burst"),
    size = 1 + rank(p, "size") * 0.35,
    speed = 1 + rank(p, "velocity") * 0.2;
  const resonance =
    1 + sides.filter((id) => rank(p, id)).length * rank(p, "resonance") * 0.12;
  let power =
    w.damage *
    attack(s, p) *
    (1 + tune * 0.25) *
    (1 + lanes * 0.35) *
    (1 + bursts * 0.3) *
    (1 + rank(p, "ricochet") * 0.15) *
    resonance *
    (evo ? 1.45 : 1);
  if (random(s) < rank(p, "critical") * 0.1) power *= 2;
  p.fireAt =
    s.tick +
    Math.max(
      4,
      Math.round(
        w.interval /
          (1 + rank(p, "rate") * 0.14) /
          (p.main === "rail" ? 1 + tune * 0.15 + rank(p, "velocity") * 0.1 : 1),
      ),
    );
  if (p.main === "tesla") {
    chain(
      s,
      p,
      p.x,
      p.y,
      power,
      3 + tune + rank(p, "chain") + (evo ? 4 : 0),
      600 + (evo ? 200 : 0),
    );
    return;
  }
  if (p.main === "laser" || p.main === "rail") {
    const count = evo && p.main === "laser" ? 3 : 1 + Math.min(1, lanes),
      radius =
        (p.main === "rail" ? 15 : 8) *
        size *
        (1 + tune * 0.12) *
        (evo ? 1.5 : 1);
    for (let i = 0; i < count; i++)
      bullet(
        s,
        p.id,
        p.main,
        p.x + (i - (count - 1) / 2) * 46,
        p.y - 30,
        0,
        -1300,
        power * (1+rank(p,'pierce')*.18) / count,
        radius,
        6,
        100,
      );
    return;
  }
  let count =
    w.count +
    lanes +
    bursts +
    (p.main === "fan" ? tune * 2 : 0) +
    (evo
      ? p.main === "rocket"
        ? 1
        : p.main === "fan"
          ? 4
          : p.main === "straight"
            ? 2
            : p.main === "double"
              ? 2
              : 1
      : 0);
  const spread =
    p.main === "fan"
      ? 0.16 / (1 + tune * 0.12)
      : p.main === "flame"
        ? 0.12
        : p.main === "double"
          ? 0.09
          : 0.028;
  const life =
    p.main === "fan"
      ? Math.round(43 * speed)
      : p.main === "flame"
        ? Math.round((22 + tune * 5 + (evo ? 12 : 0)) * speed)
        : 180;
  for (let i = 0; i < count; i++) {
    const a = (i - (count - 1) / 2) * spread,
      v = (p.main === "rocket" ? 6 : p.main === "flame" ? 13 : 18) * speed;
    bullet(
      s,
      p.id,
      p.main,
      p.x + (p.main === "double" ? (i - (count - 1) / 2) * 30 : 0),
      p.y - 30,
      Math.sin(a) * v,
      -Math.cos(a) * v,
      (power * w.count) / count,
      p.main === "flame" ? 12 * size : 7 * size,
      life,
      rank(p, "pierce") * 2 + (p.main === "flame" ? 3 : 0),
      {
        splash:
          p.main === "rocket"
            ? (75 + tune * 12) * (1 + rank(p, "blast") * 0.35)
            : 0,
        turn:
          p.main === "double"
            ? 1 + tune + rank(p, "ricochet")
            : rank(p, "ricochet"),
      },
    );
  }
}
function sideFire(s: FlightState, p: Pilot) {
  for (const id of sides) {
    const n = rank(p, id);
    if (!n) continue;
    if (id === "orbit") {
      for (let i = 0; i < n + 1; i++) {
        const a = s.tick * 0.038 + (i * Math.PI * 2) / (n + 1),
          x = p.x + Math.cos(a) * 85,
          y = p.y + Math.sin(a) * 85;
        for (const b of s.bullets)
          if (b.owner === "enemy" && Math.hypot(b.x - x, b.y - y) < 22)
            b.life = 0;
        if (s.tick % 18 === 0)
          bullet(
            s,
            p.id,
            "orbit",
            x,
            y,
            0,
            0,
            (9 + n * 6) * attack(s, p),
            16,
            1,
            100,
          );
      }
      continue;
    }
    if (s.tick % Math.max(24, 96 - n * 12) !== 0) continue;
    if (id === "arc") {
      chain(
        s,
        p,
        p.x,
        p.y,
        (12 + n * 8) * attack(s, p),
        2 + n + rank(p, "chain"),
        570,
      );
      continue;
    }
    if (id === "wing") {
      for (const dx of [-60, 60])
        bullet(
          s,
          p.id,
          "wing",
          p.x + dx,
          p.y - 10,
          0,
          -16,
          (10 + n * 9) * attack(s, p),
        );
      continue;
    }
    bullet(
      s,
      p.id,
      id,
      p.x,
      p.y - 30,
      id === "boomerang" ? 3 : 0,
      -11,
      (14 + n * 12) * attack(s, p),
      10,
      id === "boomerang" ? 150 : 200,
      id === "boomerang" ? 12 : 0,
      { splash: id === "split" ? 65 * (1 + rank(p, "blast") * 0.35) : 0 },
    );
  }
}
function director(s: FlightState) {
  const d = s.director!,
    limits = difficulty(s);
  if (s.tick >= s.nextBoss && !s.enemies.some((e) => e.boss)) {
    s.enemies = [];
    s.bullets = s.bullets.filter((b) => b.owner !== "enemy");
    spawn(s, s.bosses % BOSS_COUNT, true);
    s.nextBoss = Number.MAX_SAFE_INTEGER;
    s.banner = { text: "强敌来袭 · 留意蓄力预警", until: s.tick + 180 };
    return;
  }
  if (s.enemies.some((e) => e.boss)) return;
  if (s.tick >= d.eventUntil) {
    d.wave++;
    d.event = (["formation", "elite", "convoy", "recovery"] as const)[
      d.wave % 4
    ];
    d.eventUntil =
      s.tick +
      (d.event === "recovery" ? 720 : d.event === "formation" ? 2400 : 1800);
    d.killsAt = s.kills;
    d.eventTarget = d.event === "formation" ? 8 + d.chapter * 2 : 1;
    d.eventDone = false;
    s.banner = {
      text: {
        formation: "编队突破 · 击破" + d.eventTarget + "架敌机",
        elite: "精英悬赏 · 集中火力",
        convoy: "补给追击 · 击破运输机",
        recovery: "整备航段 · 收集补给",
      }[d.event],
      until: s.tick + 220,
    };
    if (d.event === "elite")
      spawn(
        s,
        CHAPTERS[s.scene].pool[2],
        false,
        alive(s)[0]?.x ?? 720,
        -70,
        true,
      );
    if (d.event === "convoy") spawn(s, 15, false, alive(s)[0]?.x ?? 720, -70);
    if (d.event === "recovery") {
      s.bullets = s.bullets.filter((b) => b.owner !== "enemy");
      drop(s, alive(s)[0]?.x ?? 720, 250, "repair");
    }
  }
  if (
    !d.eventDone &&
    ((d.event === "formation" && s.kills - d.killsAt >= d.eventTarget) ||
      ((d.event === "elite" || d.event === "convoy") && d.eventTarget === -1))
  ) {
    d.eventDone = true;
    s.banner = { text: "航段目标完成 · 补给已投放", until: s.tick + 160 };
    drop(s, alive(s)[0]?.x ?? 720, 300, "power");
  }
  if (s.tick < s.spawnAt || d.event === "recovery") return;
  const early = s.tick < 2400,
    pool = early ? [0, 0, 1, 2] : CHAPTERS[s.scene].pool;
  const kind = pool[Math.floor(random(s) * pool.length)];
  const serial = d.spawnSerial++,
    formation = serial % 4 === 0 && d.event === "formation";
  if (formation) {
    const center = clamp(
      (alive(s)[0]?.x ?? 720) + (random(s) - 0.5) * 440,
      120,
      WORLD_WIDTH - 120,
    );
    for (let i = 0; i < 3; i++)
      spawn(
        s,
        early ? 0 : kind,
        false,
        clamp(center + (i - 1) * 72, 40, WORLD_WIDTH - 40),
        -65 - Math.abs(i - 1) * 50,
      );
  } else {
    const sideEntry = serial % 3 === 1 && kind < 16;
    const focus = alive(s)[serial % Math.max(1, alive(s).length)]?.x ?? 720;
    spawn(
      s,
      kind,
      false,
      sideEntry
        ? clamp(focus + (serial % 2 ? -400 : 400), 30, WORLD_WIDTH - 30)
        : undefined,
      sideEntry ? 160 + random(s) * 260 : -65,
    );
    const e = s.enemies.at(-1);
    if (sideEntry && e && !e.boss) {
      e.route = "sweep";
      e.phase = serial % 2 ? 0 : Math.PI;
      e.startX = e.x;
      e.startY = e.y;
    }
  }
  s.spawnAt =
    s.tick +
    Math.round(
      limits.spawnInterval *
        (formation ? 2.1 : 1) *
        (d.event === "elite" || d.event === "convoy" ? 1.7 : 1),
    );
  d.pressure = s.bullets.filter((b) => b.owner === "enemy").length;
}
const BOSS_COUNT = 6;
function enemyAttack(s: FlightState, e: Enemy) {
  if (!e.boss && s.director?.event === "recovery") return;
  if (!e.boss && (e.kind === 0 || e.kind === 8 || e.kind === 15)) return;
  if (e.y < 70 || e.y > 790) return;
  const targets = alive(s);
  if (!targets.length) return;
  const slow = s.tick < (e.slowUntil ?? 0);
  if (e.age === e.fireAt - 50 || e.aim === undefined) {
    const p = [...targets].sort(
      (a, b) =>
        Math.hypot(a.x - e.x, a.y - e.y) - Math.hypot(b.x - e.x, b.y - e.y),
    )[0];
    if (Math.abs(p.x - e.x) > 400) return;
    e.aim = Math.atan2(p.y - e.y, p.x - e.x);
    e.warnAt = s.tick + Math.max(1, e.fireAt - e.age);
  }
  if (e.age < e.fireAt) return;
  const speed = difficulty(s).bulletSpeed * (e.elite ? 1.12 : 1),
    damage = 9 + Math.min(18, (s.tick / 3600) * 0.7),
    angle = e.aim!,
    boss = e.boss;
  const fire = (
    kind: string,
    a: number,
    v = speed,
    r = 7,
    life = 330,
    extra: Partial<Bullet> = {},
  ) =>
    bullet(
      s,
      "enemy",
      kind,
      e.x,
      e.y,
      Math.cos(a) * v,
      Math.sin(a) * v,
      damage,
      r,
      life,
      0,
      extra,
    );
  const fan = (n: number, spread: number, kind = "pellet", a = angle) => {
    for (let i = 0; i < n; i++) fire(kind, a + (i - (n - 1) / 2) * spread);
  };
  const ring = (n: number, phase: number) => {
    for (let i = 0; i < n; i++) {
      const a = (i * Math.PI * 2) / n + phase;
      if (Math.abs(Math.atan2(Math.sin(a - angle), Math.cos(a - angle))) < 0.3)
        continue;
      fire("ring", a, speed * 0.85, 10);
    }
  };
  if (boss) {
    const phase = Math.floor(e.age / 300) % 3;
    switch (e.kind) {
      case 0:
        fan(
          phase === 1 ? 7 : 5,
          0.18,
          "pellet",
          Math.PI / 2 + (phase - 1) * 0.3,
        );
        break;
      case 1:
        ring(phase === 2 ? 12 : 9, e.age * 0.009);
        break;
      case 2:
        if (phase === 1) {
          for (const dx of [-110, 110]) spawn(s, 0, false, e.x + dx, e.y + 70);
        } else fan(5, 0.24, "needle");
        break;
      case 3:
        if (phase === 0) {
          for (const a of [angle - 0.35, angle + 0.35])
            fire("enemy-beam", a, 0, 14, 95, { warmup: 65, turn: a });
        } else fan(3, 0.4, "blade", angle);
        break;
      case 4:
        if (phase === 0) ring(10, e.age * 0.006);
        else fan(3, 0.55, "orb");
        break;
      case 5:
        if (phase === 1) {
          for (let i = 0; i < 3; i++)
            fire("mine", Math.PI / 2 + (i - 1) * 0.5, 1.4, 19, 280, {
              warmup: 65,
            });
        } else fan(3, 0.28, "missile");
        break;
    }
  } else
    switch (e.kind) {
      case 1:
        fire("pellet", Math.PI / 2);
        break;
      case 2:
        fan(3, 0.2);
        break;
      case 3:
        fire("needle", angle, speed * 1.5, 6);
        break;
      case 4:
        ring(7, e.phase);
        break;
      case 5:
        fan(2, 0.32, "orb");
        break;
      case 6:
        fire("orb", Math.PI / 2, speed * 0.8, 12);
        break;
      case 7:
        if (e.age < 700) {
          spawn(s, 0, false, e.x - 32, e.y + 35);
          spawn(s, 0, false, e.x + 32, e.y + 35);
        }
        break;
      case 16:
        fire("orb", angle, speed * 0.9, 13);
        break;
      case 17:
        fan(2, 0.24, "pellet");
        break;
      case 18:
        fan(2, 0.4, "missile");
        break;
      case 19:
        fan(5, 0.16, "needle");
        break;
      case 9:
        fire("needle", angle, speed * 2.2, 6);
        break;
      case 10:
        fire("mine", Math.PI / 2, 1.2, 18, 270, { warmup: 60 });
        break;
      case 11:
        fan(2, 0.65, "blade");
        break;
      case 12:
        fire("enemy-beam", angle, 0, 12, 110, { warmup: 80, turn: angle });
        break;
      case 13:
        fire("bounce", angle, speed * 1.2, 10, 360, { turn: 2 });
        break;
      case 14:
        fan(2, 0.24, "missile");
        break;
    }
  e.fireAt =
    e.age +
    Math.round(
      ((boss ? 155 : e.kind === 9 || e.kind === 12 ? 280 : 230) *
        (slow ? 1.5 : 1)) /
        Math.min(1.5, 1 + s.tick / 108000),
    );
  e.aim = undefined;
}
function updateEnemies(s: FlightState) {
  for (const e of [...s.enemies]) {
    e.age++;
    const slow = s.tick < (e.slowUntil ?? 0) ? 0.55 : 1;
    if (e.boss) {
      e.y = q(Math.min(220, e.y + 1.5));
      const center =
          alive(s).reduce((n, p) => n + p.x, 0) / Math.max(1, alive(s).length),
        targetX = clamp(
          center +
            Math.sin(e.age * (e.kind === 3 ? 0.006 : 0.008)) *
              Math.min(230, e.kind === 5 ? 150 : 220),
          90,
          WORLD_WIDTH - 90,
        );
      e.x = q(e.x + clamp(targetX - e.x, -3, 3));
      if (e.kind === 0 && e.age % 700 > 550)
        e.y = q(220 + Math.sin((((e.age % 700) - 550) / 150) * Math.PI) * 260);
    } else {
      const baseY = e.startY ?? -65,
        baseX = e.startX ?? e.x;
      const travel = (e.routeClock = (e.routeClock ?? 0) + slow);
      if (e.altitude === "ground") {
        e.y = q(baseY + travel * 0.72);
        if (e.kind === 17)
          e.x = q(
            clamp(baseX + Math.sin(travel * 0.007) * 130, 35, WORLD_WIDTH - 35),
          );
        if (e.kind === 19)
          e.x = q(
            clamp(baseX + Math.sin(travel * 0.003) * 250, 35, WORLD_WIDTH - 35),
          );
      } else if (e.route === "sweep") {
        const direction = Math.cos(e.phase) > 0 ? 1 : -1;
        e.x = q(
          clamp(
            baseX +
              direction *
                Math.sin((Math.min(travel / 400, 1) * Math.PI) / 2) *
                420,
            28,
            WORLD_WIDTH - 28,
          ),
        );
        e.y = q(baseY + travel * 0.85 + Math.sin(travel * 0.014) * 55);
      } else if (e.route === "orbit") {
        e.x = q(
          clamp(
            baseX + Math.sin(travel * 0.017 + e.phase) * 125,
            28,
            WORLD_WIDTH - 28,
          ),
        );
        e.y = q(baseY + travel * 1.1 + Math.cos(travel * 0.017 + e.phase) * 80);
      } else if (e.route === "pincer") {
        e.x = q(
          clamp(baseX + Math.sin(travel * 0.007) * 210, 28, WORLD_WIDTH - 28),
        );
        e.y = q(baseY + travel * 1.25);
      } else {
        const v =
          (e.kind === 8
            ? travel < 110
              ? 0.7
              : 7
            : e.kind === 15
              ? 1.15
              : 1.35 + Math.min(1, s.tick / 54000)) * slow;
        e.y = q(e.y + v);
        if (e.kind === 15)
          e.x = q(
            clamp(
              baseX + Math.sin(travel * 0.01 + e.phase) * 110,
              28,
              WORLD_WIDTH - 28,
            ),
          );
      }
    }

    if (e.burnUntil && s.tick < e.burnUntil && s.tick % 30 === 0) {
      const source = s.pilots.find((p) => p.id === e.source);
      damage(
        s,
        e,
        (4 + rank(source ?? s.pilots[0], "burn") * 4) *
          attack(s, source ?? s.pilots[0]),
        source,
        true,
      );
      if (!s.enemies.includes(e)) continue;
    }
    enemyAttack(s, e);
    for (const p of alive(s))
      if (
        e.altitude !== "ground" &&
        Math.hypot(p.x - e.x, p.y - e.y) < e.radius + FIELD.core
      )
        hurt(s, p, 18);
    if (e.y > 1370) s.enemies.splice(s.enemies.indexOf(e), 1);
  }
}
function updateBullets(s: FlightState) {
  for (const b of [...s.bullets]) {
    if (b.life <= 0) continue;
    b.age = (b.age ?? 0) + 1;
    if (b.kind === "laser" || b.kind === "rail") {
      const p = s.pilots.find((p) => p.id === b.owner);
      if (p)
        for (const e of [...s.enemies])
          if (
            e.y > 20 &&
            !b.hits.includes(e.id) &&
            legacy.segmentDistance(b.x, b.y, b.x, -30, e.x, e.y) <
              e.radius + b.radius
          ) {
            b.hits.push(e.id);
            damage(s, e, b.damage, p);
          }
      b.life--;
      continue;
    }
    if (b.kind === "enemy-beam") {
      b.life--;
      const len = 1450,
        angle =
          (b as Bullet).vx === 0 && (b as Bullet).vy === 0
            ? (b.turn ?? Math.PI / 2)
            : Math.atan2(b.vy, b.vx);
      if ((b.warmup ?? 0) > 0) {
        b.warmup!--;
        continue;
      }
      for (const p of alive(s))
        if (
          legacy.segmentDistance(
            b.x,
            b.y,
            b.x + Math.cos(angle) * len,
            b.y + Math.sin(angle) * len,
            p.x,
            p.y,
          ) <
          b.radius + FIELD.core
        )
          hurt(s, p, b.damage * 1.3);
      continue;
    }
    if (b.kind === "mine" && (b.warmup ?? 0) > 0) b.warmup!--;
    const enemy = b.owner === "enemy",
      p = s.pilots.find((p) => p.id === b.owner);
    if (
      ["homing", "rocket"].includes(b.kind) ||
      (b.kind === "missile" && b.age < 80)
    ) {
      const target = enemy
        ? alive(s)[0]
        : s.enemies
            .filter((e) => e.y > 0)
            .sort(
              (a, c) =>
                Math.hypot(a.x - b.x, a.y - b.y) -
                  Math.hypot(c.x - b.x, c.y - b.y) || a.id - c.id,
            )[0];
      if (target) {
        const a = Math.atan2(target.y - b.y, target.x - b.x),
          v = enemy ? difficulty(s).bulletSpeed : 9;
        b.vx = q(b.vx * 0.87 + Math.cos(a) * v * 0.13);
        b.vy = q(b.vy * 0.87 + Math.sin(a) * v * 0.13);
      }
    }
    if (b.kind === "boomerang" || b.kind === "blade") {
      b.vy = q(b.vy + (enemy ? -0.02 : 0.15));
    }
    if (b.kind === "flame") b.radius += 0.22;
    const nx = q(b.x + b.vx),
      ny = q(b.y + b.vy);
    b.previousX = b.x;
    b.previousY = b.y;
    if (enemy) {
      if (!b.warmup)
        for (const target of alive(s))
          if (
            legacy.segmentDistance(b.x, b.y, nx, ny, target.x, target.y) <
            b.radius + FIELD.core
          ) {
            hurt(s, target, b.damage);
            b.life = 0;
            break;
          }
    } else if (p)
      for (const e of [...s.enemies]) {
        if (
          e.y < 20 ||
          b.hits.includes(e.id) ||
          legacy.segmentDistance(b.x, b.y, nx, ny, e.x, e.y) >=
            e.radius + b.radius
        )
          continue;
        b.hits.push(e.id);
        let power = b.damage;
        if (
          p.main === "fan" &&
          evolved(p) &&
          Math.hypot(e.x - p.x, e.y - p.y) < 320
        )
          power *= 2;
        damage(s, e, power, p);
        if (b.splash) {
          effect(s, e.x, e.y, "burst");
          for (const other of [...s.enemies])
            if (
              other !== e &&
              Math.hypot(other.x - e.x, other.y - e.y) < b.splash
            )
              damage(
                s,
                other,
                b.damage * 0.65 * (1 + rank(p, "blast") * 0.2),
                p,
              );
        }
        if (b.kind === "split" || (b.kind === "rocket" && evolved(p)))
          for (let i = 0; i < 4; i++)
            bullet(
              s,
              p.id,
              "fragment",
              e.x,
              e.y,
              Math.cos((i * Math.PI) / 2) * 9,
              Math.sin((i * Math.PI) / 2) * 9,
              b.damage * 0.3,
              6,
              35,
            );
        if (b.pierce-- <= 0) {
          b.life = 0;
          break;
        }
      }
    b.x = nx;
    b.y = ny;
    b.life--;
    if (
      (b.x < 16 || b.x > WORLD_WIDTH - 16) &&
      (b.turn ?? 0) > 0 &&
      b.kind !== "enemy-beam"
    ) {
      b.x = clamp(b.x, 16, WORLD_WIDTH - 16);
      b.vx = -b.vx;
      b.turn!--;
      b.hits = [];
    }
  }
  s.bullets = s.bullets.filter(
    (b) =>
      b.life > 0 &&
      b.x > -120 &&
      b.x < WORLD_WIDTH + 120 &&
      b.y > -150 &&
      b.y < 1430,
  );
}
function pickups(s: FlightState) {
  for (const item of s.pickups!) {
    item.life--;
    item.y += 1.1;
    const p = alive(s).sort(
      (a, b) =>
        Math.hypot(a.x - item.x, a.y - item.y) -
        Math.hypot(b.x - item.x, b.y - item.y),
    )[0];
    if (!p) continue;
    const d = Math.hypot(p.x - item.x, p.y - item.y);
    if (d < 100 + rank(p, "magnet") * 140) {
      item.x += (p.x - item.x) * 0.12;
      item.y += (p.y - item.y) * 0.12;
    }
    if (d < 35) {
      item.life = 0;
      if (item.kind === "weapon") {
        p.offeredWeapon = item.weapon;
        effect(s, p.x, p.y, "supply", "获得 " + weaponInfo(item.weapon!).name);
      } else {
        for (const member of alive(s)) {
          if (item.kind === "repair")
            member.hp = Math.min(member.maxHp, member.hp + 25);
          if (item.kind === "shield") member.shield += 25;
          if (item.kind === "power") member.overdriveUntil = s.tick + 480;
          member.hp = Math.min(
            member.maxHp,
            member.hp + rank(member, "salvage") * 12,
          );
        }
        effect(
          s,
          p.x,
          p.y,
          "supply",
          item.kind === "power"
            ? "火力翻倍 8秒"
            : item.kind === "repair"
              ? "修复 +25"
              : "护盾 +25",
        );
      }
    }
  }
  s.pickups = s.pickups!.filter((i) => i.life > 0 && i.y < 1330);
}
export function stepFlight(s: FlightState) {
  if (s.version === 1) {
    legacy.stepFlight(s);
    return;
  }
  if (s.phase !== "playing") return;
  s.tick++;
  for (const p of s.pilots) {
    if (p.left) continue;
    if (p.hp <= 0) {
      const near = alive(s).some(
        (other) => Math.hypot(other.x - p.x, other.y - p.y) < 85,
      );
      p.rescue = near ? p.rescue + 1 : 0;
      if (p.rescue >= 180) {
        p.hp = p.maxHp * 0.5;
        p.invulnerable = 120;
        p.rescue = 0;
        effect(s, p.x, p.y, "rescue");
      }
      continue;
    }
    p.x = p.tx;
    p.y = p.ty;
    p.cooldown = Math.max(0, p.cooldown - 1);
    p.invulnerable = Math.max(0, p.invulnerable - 1);
    p.hp = Math.min(p.maxHp, p.hp + rank(p, "regen") * 0.01);
    if (rank(p, "barrier") && s.tick % 600 === 0)
      p.shield = Math.min(60, p.shield + 12);
    if (s.tick > (p.comboUntil ?? 0)) p.combo = 0;
    shootPilot(s, p);
    sideFire(s, p);
  }
  director(s);
  updateEnemies(s);
  updateBullets(s);
  pickups(s);
  for (const e of s.effects) e.age++;
  s.effects = s.effects.filter(
    (e) =>
      e.age <
      (e.label ? 100 : e.kind === "chain" ? 12 : e.kind === "ink" ? 36 : 25),
  );
  checkEnd(s);
  const d = s.director!;
  if (
    s.phase === "playing" &&
    s.xp >= xpRequired(s.level) &&
    s.tick - d.lastUpgrade >= (s.level === 1 ? 1800 : 2400) &&
    !s.enemies.some((e) => e.boss)
  ) {
    s.xp -= xpRequired(s.level);
    s.level++;
    d.lastUpgrade = s.tick;
    s.phase = "choosing";
    for (const p of s.pilots.filter((p) => !p.left)) offer(s, p);
  }
}
export function replayCommands(
  s: FlightState,
  commands: FlightCommand[],
  until: number,
) {
  if (s.version === 1) return legacy.replayCommands(s, commands, until);
  if (!Number.isSafeInteger(until) || until < s.tick || until - s.tick > 3600)
    throw new Error("日志分块时间范围无效");
  let last = s.tick;
  for (const c of commands) {
    if (!Number.isSafeInteger(c.tick) || c.tick < last || c.tick > until)
      throw new Error("操作时间顺序无效");
    while (s.tick < c.tick) {
      if (s.phase !== "playing") throw new Error("暂停或结束后不能推进战斗");
      stepFlight(s);
    }
    if (!flightAction(s, c.player, c.action)) throw new Error("战斗操作无效");
    last = c.tick;
  }
  while (s.tick < until) {
    if (s.phase !== "playing")
      throw new Error("战斗日志缺少升级选择或包含结束后的时间");
    stepFlight(s);
  }
  return s;
}
